import axios from "axios"
import memoizedRefreshToken from "./refreshToken"
import { ToolkitStore } from "@reduxjs/toolkit/dist/configureStore"
import dayjs from "dayjs"
import {
  transformObjectKeysToCamelCase,
  transformObjectKeysToSnakeCase,
} from "./converter"
import { setZOZOFitAxiosToken } from "./zozoFitRequester"
import { notifyAppOutdate } from "features/notification/notificationSlice"
import { isAuthError } from "utils"

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Timezone-Offset": dayjs().format("Z").split(":")[0],
    "X-Client-Version": import.meta.env.PACKAGE_VERSION,
  },
})

export function setAxiosToken(token: string) {
  instance.defaults.headers.common.Authorization = "Bearer " + token
}

export function removeAxiosToken() {
  delete instance.defaults.headers.common.Authorization
}

export const notifyIfAppOutDate = (error: any, store: ToolkitStore) => {
  const status = error?.response?.status
  const message = error?.response?.data?.message
  const APP_OUT_DATE_PREFIX_MESSAGE = "This version of the client"
  const isAppOutDateError =
    status === 403 && message?.includes(APP_OUT_DATE_PREFIX_MESSAGE)

  if (isAppOutDateError) {
    store.dispatch(notifyAppOutdate())
  }

  return isAppOutDateError
}

const responseBody = (res: any) => transformObjectKeysToCamelCase(res.data)

const requester = {
  get: (url: string, params?: any, config = {}) =>
    instance
      .get(url, {
        params,
        ...config,
      })
      .then(responseBody),

  post: (url: string, data: any, config = {}) =>
    instance.post(url, data, config).then(responseBody),
  put: (url: string, data: any, config = {}) =>
    instance.put(url, data, config).then(responseBody),
  delete: (url: string, data?: any) =>
    instance.delete(url, { data }).then(responseBody),
  patch: (url: string, data: any) =>
    instance.patch(url, data).then(responseBody),
}

export const setupInstanceInterceptor = (store: ToolkitStore) => {
  instance.interceptors.response.use(
    (response) => {
      if (response.data) {
        response.data = transformObjectKeysToCamelCase(response.data)
      }

      return response
    },
    async (error) => {
      const config = error?.config

      const isAppOutDateError = notifyIfAppOutDate(error, store)
      if (isAppOutDateError) {
        return new Promise(() => {})
      }

      const isAuthEndpoints = config?.url?.includes("/accounts/token")
      const refreshToken = store.getState().auth.refreshToken

      if (isAuthError(error) && !config?.sent && !isAuthEndpoints && refreshToken) {
        config.sent = true

        const result = await memoizedRefreshToken(store)

        if (result?.accessToken) {
          config.headers = {
            ...config.headers,
            authorization: `Bearer ${result.accessToken}`,
          }
          setAxiosToken(result.accessToken)
          setZOZOFitAxiosToken(result.accessToken)
          return instance(config)
        }

        return new Promise(() => {})
      }

      return Promise.reject(error)
    },
  )

  instance.interceptors.request.use(
    (config) => {
      if (config.url?.includes("/accounts/token")) {
        return config
      }

      const isMultipartFormData =
        config.headers["Content-Type"] === "multipart/form-data" &&
        config.data instanceof FormData

      if (config.params) {
        config.params = transformObjectKeysToSnakeCase(config.params)
      }

      if (isMultipartFormData) {
        return config
      }

      if (config.data) {
        config.data = transformObjectKeysToSnakeCase(config.data)
      }

      return config
    },
    (error) => {
      return Promise.reject(error)
    },
  )
}

export default requester
